import styles from '@/components/book/Arrange.module.css';

export interface ArrangeOption<V extends string> {
  value: V;
  label: string;
  /** A trailing count; a count of zero disables the option. */
  count?: number;
}

interface ArrangeProps<V extends string> {
  label?: string;
  options: readonly ArrangeOption<V>[];
  value: V;
  onChange: (value: V) => void;
  className?: string;
}

/**
 * Words that switch how a list is arranged (`A–Z · By shelf`, the Contents
 * views). The pressed one is underlined; an option with a zero count is
 * out of the tab order and does nothing.
 */
export function Arrange<V extends string>({ label = 'Arrange', options, value, onChange, className }: ArrangeProps<V>) {
  return (
    <div className={[styles.arr, className ?? ''].filter(Boolean).join(' ')} role="group" aria-label={label}>
      {options.map((o) => {
        const off = o.count === 0;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={o.value === value}
            aria-disabled={off || undefined}
            tabIndex={off ? -1 : undefined}
            onClick={() => !off && o.value !== value && onChange(o.value)}
          >
            {o.label}
            {o.count != null && <span className={styles.n}>{o.count}</span>}
          </button>
        );
      })}
    </div>
  );
}
